import { useState, useCallback, memo } from 'react';
import { ChevronRight, ChevronDown, Copy, Check } from 'lucide-react';
import { cn } from '@/lib/utils';

interface TaskJsonViewProps {
  value: unknown;
  className?: string;
  /** Profundidade inicial expandida. Default 1. */
  initialDepth?: number;
}

export const TaskJsonView = memo(function TaskJsonView({
  value,
  className,
  initialDepth = 1,
}: TaskJsonViewProps) {
  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback(() => {
    void navigator.clipboard
      .writeText(JSON.stringify(value, null, 2))
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
      })
      .catch(() => {
        /* clipboard pode estar bloqueado em alguns contextos */
      });
  }, [value]);

  if (value === null || value === undefined) {
    return <span className="text-raios-muted text-xs">—</span>;
  }

  return (
    <div
      className={cn(
        'relative rounded-md border border-raios-elevated/30 bg-raios-bg/40 p-2 font-mono text-[11px] overflow-x-auto',
        className,
      )}
    >
      <button
        onClick={handleCopy}
        aria-label="Copiar JSON"
        className="absolute top-1.5 right-1.5 z-10 p-1 rounded text-raios-muted hover:text-raios-text hover:bg-raios-elevated/50 transition-colors"
      >
        {copied ? <Check className="h-3 w-3 text-raios-success-text" /> : <Copy className="h-3 w-3" />}
      </button>
      <JsonNode value={value} depth={0} initialDepth={initialDepth} keyPath="$" />
    </div>
  );
});

interface JsonNodeProps {
  value: unknown;
  depth: number;
  initialDepth: number;
  keyPath: string;
}

function JsonNode({ value, depth, initialDepth, keyPath }: JsonNodeProps) {
  const [open, setOpen] = useState(depth < initialDepth);

  if (value === null) return <span className="text-raios-muted">null</span>;
  if (value === undefined) return <span className="text-raios-muted">undefined</span>;

  const t = typeof value;
  if (t === 'string') return <span className="text-raios-success-text">"{value as string}"</span>;
  if (t === 'number') return <span className="text-raios-accent-text">{String(value)}</span>;
  if (t === 'boolean') return <span className="text-raios-warning-text">{String(value)}</span>;
  if (t === 'bigint') return <span className="text-raios-accent-text">{String(value)}n</span>;
  // Tipos esquisitos (function, symbol) NÃO podem virar React child cru —
  // React #31 dispara. Stringify defensivo aqui mesmo.
  if (t === 'function') return <span className="text-raios-muted">[function]</span>;
  if (t === 'symbol') return <span className="text-raios-muted">{(value as symbol).toString()}</span>;

  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="text-raios-muted">[]</span>;
    return (
      <div className="inline-flex flex-col">
        <button
          onClick={() => setOpen((o) => !o)}
          className="inline-flex items-center gap-1 text-raios-muted hover:text-raios-text"
        >
          {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
          <span>[ {value.length} ]</span>
        </button>
        {open && (
          <ul className="ml-4 border-l border-raios-elevated/40 pl-2">
            {value.map((v, i) => (
              <li key={`${keyPath}.${i}`} className="flex gap-1.5">
                <span className="text-raios-muted">{i}:</span>
                <JsonNode value={v} depth={depth + 1} initialDepth={initialDepth} keyPath={`${keyPath}.${i}`} />
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  // Objetos especiais que NÃO devem ser tratados como POJO (Date, Map, Set,
  // RegExp, Error, Promise, etc). Object.entries deles retorna [] → render
  // {} sem informação. Stringify defensivo dá visibilidade.
  if (value instanceof Date) {
    return <span className="text-raios-success-text">"{value.toISOString()}"</span>;
  }
  if (value instanceof RegExp) {
    return <span className="text-raios-warning-text">{value.toString()}</span>;
  }
  if (value instanceof Error) {
    return <span className="text-raios-danger-text">Error: {value.message || '(sem mensagem)'}</span>;
  }
  if (value instanceof Map) {
    const arr = Array.from((value as Map<unknown, unknown>).entries());
    return (
      <JsonNode
        value={Object.fromEntries(arr.map(([k, v], i) => [typeof k === 'string' ? k : `key_${i}`, v]))}
        depth={depth}
        initialDepth={initialDepth}
        keyPath={keyPath}
      />
    );
  }
  if (value instanceof Set) {
    return (
      <JsonNode
        value={Array.from(value as Set<unknown>)}
        depth={depth}
        initialDepth={initialDepth}
        keyPath={keyPath}
      />
    );
  }

  // object — Object.entries pode jogar em proxies/objetos exóticos com getters
  // que falham. Try/catch evita derrubar a tab inteira por payload sujo.
  let entries: [string, unknown][];
  try {
    entries = Object.entries(value as Record<string, unknown>);
  } catch (err) {
    return (
      <span className="text-raios-muted" title={err instanceof Error ? err.message : ''}>
        [object não-serializável]
      </span>
    );
  }
  if (entries.length === 0) return <span className="text-raios-muted">{'{}'}</span>;
  return (
    <div className="inline-flex flex-col">
      <button
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1 text-raios-muted hover:text-raios-text"
      >
        {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        <span>{'{ '}{entries.length}{' }'}</span>
      </button>
      {open && (
        <ul className="ml-4 border-l border-raios-elevated/40 pl-2">
          {entries.map(([k, v]) => (
            <li key={`${keyPath}.${k}`} className="flex gap-1.5">
              <span className="text-raios-text">{k}:</span>
              <JsonNode value={v} depth={depth + 1} initialDepth={initialDepth} keyPath={`${keyPath}.${k}`} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
